import { StringFormatter } from '@agoda/react-handyman';
import { Pax } from 'gql/autogen/Pax';
import { PaxTypeCode } from 'gql/autogen/PaxTypeCode';
import { BookingRequirement } from 'gql/autogen/BookingRequirement';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { EnrichedBookingRequirement } from 'component/page/detail/store/availabilityIndex.types';
import { PaxRequirement } from 'gql/autogen/PaxRequirement';
import { PaxCounterCms, PaxStringCmsCodes } from 'core/utils/PaxCounterCms';
import { getPax } from 'core/utils/getPax';
import { getBootstrapConfig } from 'core/window/windowService';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export interface PaxCounterProps {
    amount: number;
    title: string;
    paxType: PaxTypeCode;
    paxReqMinPax: number;
    paxReqMaxPax: number;
    minPaxWhenSelected: number;
    paxIncrementalStep: number;
}

const getCounterTitle = (cms: CmsValue, paxRequirement: PaxRequirement) => {
    if (!paxRequirement.code) return '';

    if (paxRequirement.code === PaxTypeCode.INFANT && paxRequirement.minAge === undefined) {
        return `${cms[PaxCounterCms[paxRequirement.code]]} (${StringFormatter.format(cms[130816], paxRequirement.maxAge || 0)})`;
    }

    const ages = StringFormatter.format(cms[134325], paxRequirement.minAge || 0, paxRequirement.maxAge || 0);
    return `${cms[PaxCounterCms[paxRequirement.code]]} ${ages}`;
};

export const getCounterProps = (cms: CmsValue, paxState: Pax[]) => {
    return (paxRequirement: PaxRequirement, availabilityQuantity = Infinity): PaxCounterProps => {
        const title = getCounterTitle(cms, paxRequirement);
        const thisState = paxState.filter((x) => (x.code as PaxTypeCode) === paxRequirement.code)[0];
        const amount = (thisState && thisState.quantity.value) || paxRequirement.minPax || 0;
        // Max value is the minimum of the availability quantity or the max pax requirement
        const maxValue = Math.min(availabilityQuantity, paxRequirement.maxPax ?? 1);
        return {
            amount,
            title,
            paxType: paxRequirement.code || PaxTypeCode.UNKNOWN,
            paxReqMinPax: paxRequirement.minPax ?? 0,
            paxReqMaxPax: maxValue,
            minPaxWhenSelected: paxRequirement.minPaxWhenSelected ?? paxRequirement.minPax ?? 0,
            paxIncrementalStep: paxRequirement.paxIncrementalStep ?? 1,
        };
    };
};

const DEFAULT_ADULT_PAX_AMOUNT = 1;

const createPax = (code: PaxTypeCode, quantity: number): Pax => {
    return {
        code: code.toString(),
        quantity: { value: quantity },
    };
};

export const initialPax = (paxRequirement: PaxRequirement, isAct4603: boolean, isDefaultPax?: boolean): Pax => {
    const fallbackPaxAmount = paxRequirement.paxIncrementalStep || 1;
    const minPaxWhenSelected = paxRequirement.minPaxWhenSelected || 0;
    const minPax = paxRequirement.minPax || 0;
    const finalMinPax = Math.max(minPax, minPaxWhenSelected);
    const paxCode = paxRequirement.code || PaxTypeCode.UNKNOWN;
    if (isDefaultPax) {
        if (isAct4603) {
            return finalMinPax > DEFAULT_ADULT_PAX_AMOUNT ? createPax(paxCode, finalMinPax) : createPax(paxCode, fallbackPaxAmount);
        }
        return minPaxWhenSelected > DEFAULT_ADULT_PAX_AMOUNT ? createPax(paxCode, minPaxWhenSelected) : createPax(paxCode, fallbackPaxAmount);
    }
    return createPax(paxCode, minPax);
};

const getInboundMinMax = (value: number, min: number, max: number): number => {
    let inbound = value;
    inbound = Math.min(inbound, max);
    inbound = Math.max(inbound, min);
    return inbound;
};

const initalPaxFromState = (paxRequirement: PaxRequirement, isAct4603: boolean, statePax: Pax[]): Pax => {
    const minPaxWhenSelected = paxRequirement.minPaxWhenSelected || 0;
    const minPax = paxRequirement.minPax || 0;
    const maxPax = paxRequirement.maxPax || 1;
    const finalMinPax = Math.max(minPax, minPaxWhenSelected);
    const paxCode = paxRequirement.code || PaxTypeCode.UNKNOWN;
    const existingPax = statePax.find((pax) => (pax.code as PaxTypeCode) === paxCode);
    if (existingPax) {
        const quantity = getInboundMinMax(existingPax.quantity.value, isAct4603 ? finalMinPax : minPaxWhenSelected, maxPax);
        return createPax(paxCode, quantity);
    }
    return createPax(paxCode, minPax);
};

export const getTotalPax = (passengers: Pax[]) => {
    return passengers.map((passenger) => passenger.quantity.value).reduce((prev, curr) => prev + curr, 0);
};

const paxMapper = (paxType: PaxTypeCode, value: number): Pax => {
    return {
        code: paxType,
        quantity: {
            value,
        },
    };
};

export const getCounterState = (paxState: Pax[], paxRequirement: PaxRequirement, value: number): Pax[] => {
    if (!paxRequirement.code) {
        return paxState;
    }
    const ret: Pax[] = paxState.filter((pax) => (pax.code as PaxTypeCode) !== paxRequirement.code);
    ret.push(paxMapper(paxRequirement.code, value));
    return ret;
};

const PaxCodeOrder: Record<PaxTypeCode, number> = {
    PERSON: 1,
    ADULT: 2,
    SENIOR: 3,
    YOUTH: 4,
    CHILD: 5,
    INFANT: 6,
    TODDLER: 7,
    STUDENT: 8,
    TICKET: 9,
    BOOK: 10,
    UNKNOWN: 11,
};

export const getSortedPaxRequirements = (paxRequirements: PaxRequirement[]): PaxRequirement[] => {
    if (paxRequirements.length === 0) {
        return [];
    }

    // Combine filter and reduce operations into a single reduce call
    const paxRequirementByCode = paxRequirements.reduce<Record<string, PaxRequirement>>((result: Record<string, PaxRequirement>, paxRequirement: PaxRequirement) => {
        if (paxRequirement?.code && paxRequirement?.maxPax && paxRequirement?.maxPax > 0) {
            result[paxRequirement.code] = paxRequirement;
        }
        return result;
    }, {});

    // Swap out for-in loop with Array.map call for better readability
    const sortedPaxRequirements = Object.keys(PaxCodeOrder)
        .map((paxCode) => paxRequirementByCode[paxCode])
        .filter(Boolean);

    return sortedPaxRequirements;
};

export const initialPaxState = (bookingRequirement: EnrichedBookingRequirement, isAct4603: boolean, paxState?: Pax[]): Pax[] => {
    const paxRequirements = bookingRequirement.paxRequirements;
    const sortedPaxRequirements = getSortedPaxRequirements(paxRequirements);
    const isPaxStateExist = paxState && paxState.length > 0;
    const pax = sortedPaxRequirements.map((paxRequirement, index) =>
        (isAct4603 ? isPaxStateExist : paxState) ? initalPaxFromState(paxRequirement, isAct4603, paxState || []) : initialPax(paxRequirement, isAct4603, index === 0),
    );
    const totalPax = getTotalPax(pax);

    const minPaxFromBookingRequirement = (bookingRequirement && bookingRequirement.minPax) || 0;
    const firstIndexPaxValue = pax[0]?.quantity.value || 0;
    const totalPaxWithOutFirstIndex = totalPax - firstIndexPaxValue;
    const paxDiffFromBookingRequirement = minPaxFromBookingRequirement - totalPaxWithOutFirstIndex;
    if (paxDiffFromBookingRequirement > 0 && totalPax < minPaxFromBookingRequirement) {
        pax[0].quantity.value = paxDiffFromBookingRequirement;
    }
    const filteredPax = pax.filter((p) => p.quantity.value > 0);
    return filteredPax;
};

export const sortedTotalPax = (pax?: Pax[]): Pax[] => {
    if (!pax) {
        return [];
    }
    return pax.slice().sort((a, b) => PaxCodeOrder[a.code as PaxTypeCode] - PaxCodeOrder[b.code as PaxTypeCode]);
};

export const mapPaxByCode = (counterState: Pax[]): Record<string, Pax> => {
    if (!counterState || counterState.length === 0) {
        return {};
    }
    return counterState.reduce<Record<string, Pax>>((paxMap, pax) => {
        paxMap[pax.code] = pax;
        return paxMap;
    }, {});
};

export enum AvailabilitySearchCriteriaStatus {
    NoError = 'no_error',
    MaxPaxExceed = 'max_pax_exceed',
    MaxAvailableExceed = 'max_available_exceed',
    MinPaxRequired = 'min_pax_required',
}

export const getAvailabilitySearchCriteriaStatus = (
    bookingRequirement: Required<EnrichedBookingRequirement>,
    counterState: Pax[],
    totalPax: number,
    maxAvailable = Infinity,
): AvailabilitySearchCriteriaStatus => {
    if (totalPax >= bookingRequirement.maxPax) {
        return AvailabilitySearchCriteriaStatus.MaxPaxExceed;
    }
    if (totalPax >= maxAvailable) {
        return AvailabilitySearchCriteriaStatus.MaxAvailableExceed;
    }
    const paxByCode = mapPaxByCode(counterState);
    let minPaxRequired = false;
    let totalAdult = 0;
    if (bookingRequirement.adultRequired) {
        const senior = paxByCode.SENIOR ? paxByCode.SENIOR.quantity.value : 0;
        const adult = paxByCode.ADULT ? paxByCode.ADULT.quantity.value : 0;
        const person = paxByCode.PERSON ? paxByCode.PERSON.quantity.value : 0; // Some activities need adult but does not have Adult or Senior in PAX list.
        totalAdult = senior + adult + person;
        minPaxRequired = totalAdult < (bookingRequirement.minPax > 0 ? bookingRequirement.minPax : 1);
    } else {
        minPaxRequired = totalPax < bookingRequirement.minPax;
    }
    if (minPaxRequired && (totalAdult <= 0 || totalPax < bookingRequirement.minPax)) {
        return AvailabilitySearchCriteriaStatus.MinPaxRequired;
    }
    return AvailabilitySearchCriteriaStatus.NoError;
};

const formattedComma = (cms: CmsValue, totalPasString: string[]): string => {
    const paxString = totalPasString.reduce((previousValue, currentValue, currentIndex) => {
        if (!previousValue) return currentValue;
        if (currentIndex + 1 === totalPasString.length) {
            return StringFormatter.format(cms[87776], previousValue, currentValue);
        }
        return StringFormatter.format(cms[84653], previousValue, currentValue);
    }, '');
    return paxString;
};

const getTotalPaxString = (cms: CmsValue, filterPaxTypes: PaxRequirement[], isAct4603: boolean) => {
    const totalPaxString = filterPaxTypes.map((paxType) => {
        const finalMinPax = Math.max(paxType.minPax || 0, paxType.minPaxWhenSelected || 0);
        return getPax(cms, isAct4603 ? finalMinPax : paxType.minPax, paxType.code);
    });
    return formattedComma(cms, totalPaxString);
};

const mapAdultOrSenior = (isHaveAdult: boolean, isHaveSenior: boolean) => {
    const adultSeniorCase = +isHaveSenior * 2 + +isHaveAdult;
    switch (adultSeniorCase) {
        case 0:
            return PaxTypeCode.UNKNOWN;
        case 1:
            return PaxTypeCode.ADULT;
        case 2:
            return PaxTypeCode.SENIOR;
        case 3:
            return 'AdultOrSenior';
        default:
            return PaxTypeCode.UNKNOWN;
    }
};

const getMinPassengersWhenAdultRequired = (cms: CmsValue, sortedPaxTypes: PaxRequirement[], minPax: number, isAct4603: boolean) => {
    const sortedPaxMap = sortedPaxTypes.reduce<Partial<Record<PaxTypeCode, PaxRequirement>>>((obj, pax) => {
        if (pax.code) {
            obj[pax.code] = pax;
        }
        return obj;
    }, {});

    const adultPax = sortedPaxMap[PaxTypeCode.ADULT];
    const seniorPax = sortedPaxMap[PaxTypeCode.SENIOR];
    const minAdult = adultPax?.minPax || 0;
    const minSenior = seniorPax?.minPax || 0;
    const isHaveAdult = !!adultPax;
    const isHaveSenior = !!seniorPax;
    const paxCode = mapAdultOrSenior(isHaveAdult, isHaveSenior);
    let paxCms = PaxStringCmsCodes[paxCode];
    const totalAdult = minSenior + minAdult;

    if (minAdult > 0 || minSenior > 0) {
        if (totalAdult < minPax) {
            paxCms = PaxStringCmsCodes[PaxTypeCode.PERSON];
            if (minPax > 1) {
                return StringFormatter.format(cms[paxCms.plural], minPax.toString());
            }
            return cms[paxCms.singular];
        }
        const adultsAndSeniors = [adultPax, seniorPax].filter((p) => p) as PaxRequirement[];
        return getTotalPaxString(cms, adultsAndSeniors, isAct4603);
    }

    if (minPax > 1) {
        return StringFormatter.format(cms[paxCms.plural], minPax.toString());
    }
    return cms[paxCms.singular];
};

const getMinPassengerFromEnrichedBookingRequirement = (cms: CmsValue, bookingRequirement: EnrichedBookingRequirement, isAct4603: boolean): string => {
    const sortedPaxRequirements = getSortedPaxRequirements(bookingRequirement.paxRequirements);
    const config = getBootstrapConfig();
    const isAct5500 = isB(ExperimentId.ACT_5500, config.experiments);
    // When integrate both ACT-4603 and ACT-5500, the logic will be isConsiderMinPaxWhenSelected = sortedPaxRequirements.length === 1
    const isConsiderMinPaxWhenSelected = isAct4603 && (isAct5500 ? sortedPaxRequirements.length === 1 : true); // return true to keep the bug on A side of ACT-5500
    const filterPaxRequirements = isConsiderMinPaxWhenSelected
        ? sortedPaxRequirements.filter(({ minPax, minPaxWhenSelected }) => (minPax || 0) > 0 || (minPaxWhenSelected || 0) > 0)
        : sortedPaxRequirements.filter((paxRequirement) => (paxRequirement.minPax || 0) > 0);
    const totalMinPaxRequirements = isConsiderMinPaxWhenSelected
        ? filterPaxRequirements.reduce((sum, { minPax, minPaxWhenSelected }) => sum + Math.max(minPax || 0, minPaxWhenSelected || 0), 0)
        : filterPaxRequirements.reduce((sum, paxRequirement) => sum + (paxRequirement.minPax || 0), 0);
    const minPax = bookingRequirement.minPax || 0;

    if (bookingRequirement.adultRequired && sortedPaxRequirements.length > 1) {
        return getMinPassengersWhenAdultRequired(cms, sortedPaxRequirements, minPax, isAct4603);
    }
    if (minPax > totalMinPaxRequirements) {
        const defaultPaxCode = sortedPaxRequirements.length === 1 ? sortedPaxRequirements[0].code : PaxTypeCode.PERSON;
        return getPax(cms, minPax, filterPaxRequirements.length === 1 ? filterPaxRequirements[0].code : defaultPaxCode);
    }
    if (filterPaxRequirements.length > 0) {
        return getTotalPaxString(cms, filterPaxRequirements, isAct4603);
    }

    return getPax(cms, minPax, PaxTypeCode.PERSON);
};

export const getMinPassengerConstraint = (cms: CmsValue, bookingRequirement: EnrichedBookingRequirement, isAct4603: boolean): string => {
    const minPassenger = getMinPassengerFromEnrichedBookingRequirement(cms, bookingRequirement, isAct4603);
    return StringFormatter.format(cms[223438], minPassenger);
};

export const getMinPassengerError = (cms: CmsValue, bookingRequirement: EnrichedBookingRequirement, isAct4603: boolean): string => {
    const minPassenger = getMinPassengerFromEnrichedBookingRequirement(cms, bookingRequirement, isAct4603);
    return StringFormatter.format(cms[223439], minPassenger);
};

export const toEnrichedBookingRequirement = (bookingRequirement: BookingRequirement, paxRequirements: PaxRequirement[]): Required<EnrichedBookingRequirement> => {
    const paxRefId = bookingRequirement.paxRequirementRefId || [];
    const selectedPaxRequirements = paxRefId
        .map((refId) => {
            return paxRequirements.find((paxRequirement) => paxRequirement.id === refId);
        })
        .filter((paxRequirement) => paxRequirement) as PaxRequirement[];
    const sortedPaxRequirements = getSortedPaxRequirements(selectedPaxRequirements);
    return {
        id: bookingRequirement.id ?? -1,
        minPax: bookingRequirement.minPax ?? 0,
        maxPax: bookingRequirement.maxPax ?? 1,
        adultRequired: bookingRequirement.adultRequired ?? false,
        paxRequirements: sortedPaxRequirements,
    };
};

export const calculatePlus = (amount: number, minValueWhenSelected: number, paxIncrementalStep: number) => {
    const adjustedMinValueWhenSelected = minValueWhenSelected === 0 ? paxIncrementalStep : minValueWhenSelected;
    return amount === 0 ? adjustedMinValueWhenSelected : amount + paxIncrementalStep;
};

export const calculateMinus = (amount: number, minValue: number, minValueWhenSelected: number, paxIncrementalStep: number) => {
    return amount <= minValueWhenSelected ? minValue : amount - paxIncrementalStep;
};
